import { apiService } from './api';
import { Task, TaskUpdatePayload, TaskStatusDTO } from '../types/task';
import { TaskStatus } from '../types/task-status';
import { AxiosResponse } from 'axios';
import { TaskDraftDTO, TaskDraftUpdatePayload } from '../types/TaskDraftDTO';
import { TaskDecompositionRequestDTO } from '../types/TaskDecompositionRequestDTO';
import { TaskUserDTO } from '../types/TaskUserDTO';
import { User } from '../types/user';
import { TaskUserStatus } from '../types/TaskUserStatus';

const TASK_BASE_PATH = '/Task';

export const taskService = {
    getMyTasks(status?: TaskStatus, newestFirst: boolean = true): Promise<Task[]> {
        const params = new URLSearchParams();
        if (status) {
            params.append('status', status.toString());
        }
        params.append('newestFirst', newestFirst.toString());
        return apiService.get<Task[]>(`/Task/my?${params.toString()}`).then(res => res.data);
    },

    getTaskStatuses(): Promise<TaskStatusDTO[]> {
        return apiService.get<TaskStatusDTO[]>('/Task/statuses').then(res => res.data);
    },

    // --- Методы для TaskEntity (остаются без изменений) ---

    getTaskById: async (taskId: string, userId?: string, isPlatformCuratorOrAdmin?: boolean): Promise<Task> => {
        const params = new URLSearchParams();
        if (userId) {
            params.append('userId', userId);
        }
        if (isPlatformCuratorOrAdmin) {
            params.append('isPlatformCuratorOrAdmin', isPlatformCuratorOrAdmin.toString());
        }
        const url = `${TASK_BASE_PATH}/${taskId}?${params.toString()}`;
        const response = await apiService.get<Task>(url);
        return response.data;
    },

    getTasksByOrganization: async (organizationId: string, managerId: string): Promise<Task[]> => {
        const response = await apiService.get<Task[]>(`${TASK_BASE_PATH}/organization/${organizationId}?managerId=${managerId}`);
        return response.data;
    },


    updateTask: async (taskId: string, task: TaskUpdatePayload): Promise<Task> => {
        const response = await apiService.put<Task>(`${TASK_BASE_PATH}/${taskId}`, task);
        return response.data;
    },

    updateTaskStatus: async (taskId: string, newStatus: TaskStatus): Promise<Task> => {
        const response = await apiService.put<Task>(`${TASK_BASE_PATH}/${taskId}/status/${newStatus}`);
        return response.data;
    },

    getAvailableTaskStatuses: async (taskId: string, userId: string): Promise<TaskStatus[]> => {
        const response = await apiService.get<TaskStatus[]>(`${TASK_BASE_PATH}/${taskId}/statuses/available?userId=${userId}`);
        return response.data;
    },

    getAllTasksPlatform: async (): Promise<Task[]> => {
        const response = await apiService.get<Task[]>(`${TASK_BASE_PATH}/all-platform`);
        return response.data;
    },

    // --- Методы для TaskDraft (перенесены) ---

    createTaskDraft: async (taskDraft: TaskDraftDTO): Promise<TaskDraftDTO> => {
        const response = await apiService.post<TaskDraftDTO>(`${TASK_BASE_PATH}/drafts`, taskDraft);
        return response.data;
    },

    getTaskDraftsByOrganization: async (organizationId: string): Promise<TaskDraftDTO[]> => {
        const response = await apiService.get<TaskDraftDTO[]>(`${TASK_BASE_PATH}/drafts/organization/${organizationId}`);
        return response.data;
    },

    getTaskDraftById: async (taskDraftId: string): Promise<TaskDraftDTO> => {
        const response = await apiService.get<TaskDraftDTO>(`${TASK_BASE_PATH}/drafts/${taskDraftId}`);
        return response.data;
    },

    updateTaskDraft: async (taskDraftId: string, taskDraft: TaskDraftUpdatePayload): Promise<TaskDraftDTO> => {
        const response = await apiService.put<TaskDraftDTO>(`${TASK_BASE_PATH}/drafts/${taskDraftId}`, taskDraft);
        return response.data;
    },

    deleteTaskDraft: async (taskDraftId: string): Promise<AxiosResponse<void>> => {
        return apiService.delete<void>(`${TASK_BASE_PATH}/drafts/${taskDraftId}`);
    },

    // --- Метод для декомпозиции (перенесен) ---

    decomposeTask: async (request: TaskDecompositionRequestDTO): Promise<Task[]> => {
        const response = await apiService.post<Task[]>(`${TASK_BASE_PATH}/decompose`, request);
        return response.data;
    },

    getTaskUsersByTaskId: async (taskId: string): Promise<TaskUserDTO[]> => {
        const response = await apiService.get<TaskUserDTO[]>(`${TASK_BASE_PATH}/${taskId}/users`);
        return response.data;
    },

    getUsersNotInTask: async (taskId: string, managerId: string): Promise<User[]> => {
        const response = await apiService.get<User[]>(`${TASK_BASE_PATH}/${taskId}/users/not-in-task?managerId=${managerId}`);
        return response.data;
    },

    assignUserToTask: async (taskId: string, userIdToAssign: string): Promise<TaskUserDTO> => {
        const response = await apiService.post<TaskUserDTO>(`${TASK_BASE_PATH}/${taskId}/users/assign`, userIdToAssign);
        return response.data;
    },

    updateTaskUserStatus: async (taskUserId: string, newStatus: TaskUserStatus): Promise<TaskUserDTO> => {
        const response = await apiService.put<TaskUserDTO>(`${TASK_BASE_PATH}/users/${taskUserId}/status/${newStatus}`);
        return response.data;
    },

    deleteTaskUser: async (taskUserId: string): Promise<AxiosResponse<void>> => {
        return apiService.delete<void>(`${TASK_BASE_PATH}/users/${taskUserId}`);
    },

    acceptTask: async (taskUserId: string): Promise<TaskUserDTO> => {
        const response = await apiService.post<TaskUserDTO>(`${TASK_BASE_PATH}/users/accept`, { taskUserId });
        return response.data;
    },

    rejectTask: async (taskUserId: string): Promise<TaskUserDTO> => {
        const response = await apiService.post<TaskUserDTO>(`${TASK_BASE_PATH}/users/reject`, { taskUserId });
        return response.data;
    },

    getTasksByTaskDraftId: async (taskDraftId: string): Promise<Task[]> => {
        const response = await apiService.get<Task[]>(`${TASK_BASE_PATH}/drafts/${taskDraftId}/tasks`);
        return response.data;
    },
};
